import { I18n } from "@/i18n/I18n";
import { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";

interface NameDescriptionModalProps {
  onHide: () => void;
  onConfirm: (request: { name: string; description: string }) => Promise<void | { nameError?: string }>;
  currentName?: string;
  currentDescription?: string;
  title: string;
}

export function NameDescriptionModal({ onHide, onConfirm, currentName, currentDescription, title }: NameDescriptionModalProps) {
  const [name, setName] = useState(currentName || '');
  const [description, setDescription] = useState(currentDescription || '');
  const [nameError, setNameError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setName(currentName || '');
    setDescription(currentDescription || '');
    setNameError(undefined);
  }, [currentName, currentDescription]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setNameError(I18n.get('required'));
      return;
    }

    setIsSubmitting(true);
    const response = await onConfirm({ 
      name: name.trim(), 
      description: description.trim() 
    });

    if (response?.nameError) {
      setNameError(response.nameError);
      setIsSubmitting(false);
      return;
    }
    handleClose();
    setIsSubmitting(false);
};

  const handleClose = () => {
    onHide();
    setName('');
    setDescription('');
    setNameError(undefined);
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setNameError(undefined);
  };

  return (
    <Modal show onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Label>{I18n.get('name')}</Form.Label>
        <Form.Control
          size='sm'
          aria-label={I18n.get('enterField', { field: I18n.get('name') })}
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          isInvalid={!!nameError}
        />
        <Form.Control.Feedback type="invalid">
          { nameError }
        </Form.Control.Feedback>
        <Form.Label>{I18n.get('description')}</Form.Label>
        <Form.Control
          size='sm'
          aria-label={I18n.get('enterField', { field: I18n.get('description') })}
          rows={8}
          value={description}
          as='textarea'
          onChange={(e) => setDescription(e.target.value)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
          {I18n.get('cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={() => void handleSubmit()}
          disabled={isSubmitting}
        >
          {isSubmitting ? I18n.get('saving') : I18n.get('confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
